import { DropDownButton, DropdownItem, IconName, ListDivider, Stack } from '@optimus-web/core';
import { lt, showSuccessAlert } from '@mews/framework';
import { copyToClipboard } from '@mews-commander/utils';
import { ProductPricing, ServiceType } from '@mews-commander/data-access';
import { EnterpriseLinkIconButton, SuspenseBoundary, TooltippedIconButton } from '@mews-commander/ui';
import { useCurrentService } from '../../queries/useCurrentService';
import { useEditProductDialogForm } from '../../forms/useEditProductDialogForm';
import { useDeleteProductDialog } from '../../hooks/useDeleteProductDialog';
import { ProductSidePanelTab } from '../../forms/types';

interface Props {
    currentTab: ProductSidePanelTab;
    closeSidePanel: () => void;
    productId: string;
    productPricing: ProductPricing;
}
export const HeaderActions = ({ currentTab, closeSidePanel, productId, productPricing }: Props) => {
    const { id, data } = useCurrentService();
    return (
        <Stack spacing="100">
            <SuspenseBoundary>
                <EditProductFormButton currentTab={currentTab} productId={productId} />
            </SuspenseBoundary>
            {data.discriminator === ServiceType.Bookable && productPricing === ProductPricing.Absolute ? (
                <EnterpriseLinkIconButton
                    icon={IconName.Pricing}
                    to="Product/Pricing/$id"
                    params={{ id: productId }}
                    search={{
                        OwnerId: id,
                    }}
                    size="small"
                    tooltip={lt('Pricing')}
                    type="button"
                />
            ) : null}
            <MoreActions productId={productId} closeSidePanel={closeSidePanel} />
        </Stack>
    );
};

const EditProductFormButton = ({ currentTab, productId }: Pick<Props, 'productId' | 'currentTab'>) => {
    const [renderDialog, toggleDialog] = useEditProductDialogForm({ initialTab: currentTab, productId });
    return (
        <>
            <TooltippedIconButton
                icon={IconName.Edit}
                tooltip={lt('Edit')}
                size="small"
                type="button"
                onClick={toggleDialog}
            />
            {renderDialog()}
        </>
    );
};

export const MoreActions = ({
    productId,
    closeSidePanel,
    disabledDelete = false,
}: Pick<Props, 'productId' | 'closeSidePanel'> & { disabledDelete?: boolean }) => {
    const { confirmationDialog, openConfirmationDialog } = useDeleteProductDialog({
        productId,
        onSuccess: closeSidePanel,
    });
    return (
        <>
            <DropDownButton icon={IconName.More} size="small">
                <DropdownItem
                    label={lt('CopyProductId')}
                    onClick={() => {
                        if (copyToClipboard(productId)) {
                            showSuccessAlert({ description: lt('CopiedSuccessMessage') });
                        }
                    }}
                />
                {disabledDelete ? null : (
                    <>
                        <ListDivider />
                        <DropdownItem label={lt('Delete')} variant="danger" onClick={openConfirmationDialog} />
                    </>
                )}
            </DropDownButton>
            {confirmationDialog}
        </>
    );
};
